/**
 * @file 学员新建 / 编辑弹窗
 * @description 基础信息 + 按科目动态详情（至少一条）；新建时教师可空。
 *
 * @whenToUse 学员管理页创建与编辑。
 * @whenNotToUse 绑定教师、充值等其他操作弹窗。
 */

import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { GradeSelect } from '@/components/ui/grade-select.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { Input } from '@/components/ui/input.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import { Select } from '@/components/ui/select.tsx'
import {
  EMPTY_STUDENT_FORM,
  LEARNING_LEVEL_OPTIONS,
  SEMESTER_OPTIONS,
  STAGE_OPTIONS,
  SUBJECT_OPTIONS,
  TEXTBOOK_OPTIONS,
  createEmptyDetailForm,
  type StudentDetailFormValues,
  type StudentFormValues,
  type StudentRow,
} from '@/components/pages/student-page/mock.ts'
import { ScrollArea } from '@/components/ui/scroll-area'

const PHONE_PATTERN = /^1\d{10}$/

type FormErrors = {
  name?: string
  phone?: string
  points?: string
  details?: string
  detailFields?: Partial<
    Record<keyof Omit<StudentDetailFormValues, 'key'>, string>
  >[]
}

export type StudentFormDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 编辑目标；为空表示新建 */
  editing: StudentRow | null
  /** 提交 */
  onSubmit: (values: StudentFormValues) => void
}

/**
 * 学员创建 / 编辑 Dialog
 */
export function StudentFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: StudentFormDialogProps) {
  const isEdit = Boolean(editing)
  const [values, setValues] = useState<StudentFormValues>(EMPTY_STUDENT_FORM)
  const [errors, setErrors] = useState<FormErrors>({})

  useEffect(() => {
    if (!open) return
    if (editing) {
      setValues({
        name: editing.name,
        phone: editing.phone,
        points: editing.points,
        details: editing.details.map((item) => ({
          key: item.id,
          subject: item.subject,
          textbook: item.textbook,
          grade: item.grade,
          semester: item.semester,
          stage: item.stage,
          learningLevel: item.learningLevel,
          learningPower: item.learningPower,
          teacherName: item.teacherName,
        })),
      })
    } else {
      setValues({
        ...EMPTY_STUDENT_FORM,
        details: [createEmptyDetailForm()],
      })
    }
    setErrors({})
  }, [open, editing])

  const setBaseField = <K extends 'name' | 'phone' | 'points'>(
    key: K,
    value: StudentFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const setDetailField = <K extends keyof StudentDetailFormValues>(
    index: number,
    key: K,
    value: StudentDetailFormValues[K],
  ) => {
    setValues((prev) => ({
      ...prev,
      details: prev.details.map((item, i) =>
        i === index ? { ...item, [key]: value } : item,
      ),
    }))
  }

  const addDetail = () => {
    setValues((prev) => ({
      ...prev,
      details: [...prev.details, createEmptyDetailForm()],
    }))
  }

  const removeDetail = (index: number) => {
    if (values.details.length <= 1) return
    setValues((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }))
  }

  const validate = () => {
    const next: FormErrors = { detailFields: [] }
    if (!values.name.trim()) next.name = '请输入学员名称'
    const phone = values.phone.trim()
    if (!phone) next.phone = '请输入手机号'
    else if (!PHONE_PATTERN.test(phone)) next.phone = '请输入 11 位手机号'
    if (values.points < 0) next.points = '点数不能为负'
    if (values.details.length === 0) next.details = '至少保留一条科目详情'

    const subjects = values.details.map((item) => item.subject).filter(Boolean)
    const duplicated = subjects.some(
      (subject, index) => subjects.indexOf(subject) !== index,
    )
    if (duplicated) next.details = '同一学员下科目不能重复'

    next.detailFields = values.details.map((item) => {
      const field: NonNullable<FormErrors['detailFields']>[number] = {}
      if (!item.subject) field.subject = '请选择科目'
      if (!item.textbook) field.textbook = '请选择教材'
      if (!item.grade) field.grade = '请选择年级'
      if (!item.semester) field.semester = '请选择学期'
    if (!item.stage) field.stage = '请选择教育阶段'
    if (!item.learningLevel) field.learningLevel = '请选择学习力等级'
      if (item.learningPower < 0) field.learningPower = '学习力不能为负'
      return field
    })

    const hasDetailError = next.detailFields.some(
      (field) => Object.keys(field).length > 0,
    )
    setErrors(next)
    return (
      !next.name &&
      !next.phone &&
      !next.points &&
      !next.details &&
      !hasDetailError
    )
  }

  const handleSubmit = () => {
    if (!validate()) return
    onSubmit({
      ...values,
      name: values.name.trim(),
      phone: values.phone.trim(),
      details: values.details.map((item) => ({
        ...item,
        teacherName: item.teacherName.trim(),
      })),
    })
    onOpenChange(false)
  }

  const usedSubjects = values.details.map((item) => item.subject)

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? '编辑学员' : '新增学员'}
      className="max-w-3xl"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit}>{isEdit ? '保存' : '创建'}</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label="学员名称" required error={errors.name}>
            <Input
              value={values.name}
              invalid={Boolean(errors.name)}
              placeholder="请输入学员名称"
              onChange={(event) => setBaseField('name', event.target.value)}
            />
          </FormField>
          <FormField label="手机号" required error={errors.phone}>
            <Input
              value={values.phone}
              invalid={Boolean(errors.phone)}
              placeholder="请输入手机号"
              inputMode="numeric"
              maxLength={11}
              onChange={(event) =>
                setBaseField(
                  'phone',
                  event.target.value.replace(/\D/g, '').slice(0, 11),
                )
              }
            />
          </FormField>
          <FormField label="点数" required error={errors.points}>
            <InputNumber
              value={values.points}
              min={0}
              invalid={Boolean(errors.points)}
              onChange={(value) => setBaseField('points', value ?? 0)}
            />
          </FormField>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-foreground">科目详情</p>
              {errors.details ? (
                <span className="ml-2 text-danger">{errors.details}</span>
              ) : null}
            </div>
            <Button
              type="button"
              variant="secondary"
              icon={<Plus className="size-4" aria-hidden />}
              onClick={addDetail}
            >
              添加详情信息
            </Button>
          </div>

          <ScrollArea className="h-128">
            <div className="space-y-3">
              {values.details.map((detail, index) => {
                const fieldErrors = errors.detailFields?.[index] ?? {}
                const subjectOptions = SUBJECT_OPTIONS.map((option) => ({
                  ...option,
                  disabled:
                    option.value !== detail.subject &&
                    usedSubjects.includes(option.value),
                }))

                return (
                  <div
                    key={detail.key}
                    className="rounded-xl border border-border bg-surface p-4"
                  >
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-foreground">
                        详情 {index + 1}
                      </p>
                      <IconButton
                        variant="ghost"
                        aria-label={`删除详情 ${index + 1}`}
                        disabled={values.details.length <= 1}
                        icon={<Trash2 className="size-4" aria-hidden />}
                        onClick={() => removeDetail(index)}
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        label="科目"
                        required
                        error={fieldErrors.subject}
                      >
                        <Select
                          value={detail.subject || undefined}
                          invalid={Boolean(fieldErrors.subject)}
                          placeholder="请选择科目"
                          options={subjectOptions}
                          onChange={(value) =>
                            setDetailField(index, 'subject', value)
                          }
                        />
                      </FormField>
                      <FormField
                        label="教材"
                        required
                        error={fieldErrors.textbook}
                      >
                        <Select
                          value={detail.textbook || undefined}
                          invalid={Boolean(fieldErrors.textbook)}
                          placeholder="请选择教材"
                          options={[...TEXTBOOK_OPTIONS]}
                          onChange={(value) =>
                            setDetailField(index, 'textbook', value)
                          }
                        />
                      </FormField>
                      <FormField label="阶段" required error={fieldErrors.stage}>
                        <Select
                          value={detail.stage || undefined}
                          invalid={Boolean(fieldErrors.stage)}
                          placeholder="请选择教育阶段"
                          options={[...STAGE_OPTIONS]}
                          onChange={(value) =>
                            setDetailField(index, 'stage', value)
                          }
                        />
                      </FormField>
                      <FormField label="年级" required error={fieldErrors.grade}>
                        <GradeSelect
                          value={detail.grade || undefined}
                          stage={detail.stage}
                          invalid={Boolean(fieldErrors.grade)}
                          placeholder="请选择年级"
                          onChange={(value) =>
                            setDetailField(index, 'grade', value)
                          }
                        />
                      </FormField>
                      <FormField
                        label="学期"
                        required
                        error={fieldErrors.semester}
                      >
                        <Select
                          value={detail.semester || undefined}
                          invalid={Boolean(fieldErrors.semester)}
                          placeholder="请选择学期"
                          options={[...SEMESTER_OPTIONS]}
                          onChange={(value) =>
                            setDetailField(index, 'semester', value)
                          }
                        />
                      </FormField>
                      <FormField
                        label="学习力等级"
                        required
                        error={fieldErrors.learningLevel}
                      >
                        <Select
                          value={detail.learningLevel || undefined}
                          invalid={Boolean(fieldErrors.learningLevel)}
                          placeholder="请选择学习力等级"
                          options={[...LEARNING_LEVEL_OPTIONS]}
                          onChange={(value) =>
                            setDetailField(index, 'learningLevel', value)
                          }
                        />
                      </FormField>
                      <FormField
                        label="学习力"
                        required
                        error={fieldErrors.learningPower}
                      >
                        <InputNumber
                          value={detail.learningPower}
                          min={0}
                          invalid={Boolean(fieldErrors.learningPower)}
                          onChange={(value) =>
                            setDetailField(index, 'learningPower', value ?? 0)
                          }
                        />
                      </FormField>
                      <FormField
                        label="所属教师"
                        hint={isEdit ? undefined : '新建可留空，之后绑定'}
                      >
                        <Input
                          value={detail.teacherName}
                          placeholder="教师名称（可选）"
                          onChange={(event) =>
                            setDetailField(index, 'teacherName', event.target.value)
                          }
                        />
                      </FormField>
                    </div>
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        </div>
      </div>
    </Dialog>
  )
}
